import { useMemo, useEffect } from "react";
import { Select, Typography, Divider, Space, Button, message } from "antd";
import { TrendingUp, Zap, FileOutput } from "lucide-react";

const { Text } = Typography;

// Helper: checks single or ensemble models
const isModelSelected = (modalSelection, modelName) => {
  if (Array.isArray(modalSelection)) {
    return modalSelection.includes(modelName);
  }
  return modalSelection === modelName;
};

const AnalyticsConfigurationCard = ({
  modalSelection,
  dependentVariable,
  groupBy,
  onChangeModal,
  onChangeDependent,
  onChangeGroupBy,
  dependentOptions,
  groupByOptions,
  onConfigure,
  applying,
  applyResponse,
  MODEL_OPTIONS,
  ENSEMBLE_MODELS,
  onBackToDashboard,
}) => {
  /* ---------------------------------------------
   * Memoized model flags
   * --------------------------------------------- */
  const modelFlags = useMemo(() => {
    const isArray = Array.isArray(modalSelection);

    return {
      isArray,
      isBenford: isModelSelected(modalSelection, "benford"),
      isSampling: isModelSelected(modalSelection, "sampling"),
      isRSF: isModelSelected(modalSelection, "rsf"),
      isBenfordOrSampling:
        !isArray &&
        (isModelSelected(modalSelection, "benford") ||
          isModelSelected(modalSelection, "sampling")),
    };
  }, [modalSelection]);

  const {
    isArray,
    isBenford,
    isSampling,
    isRSF,
    isBenfordOrSampling,
  } = modelFlags;

  /* ---------------------------------------------
   * Optional UX fix:
   * Clear groupBy when not required
   * --------------------------------------------- */
  useEffect(() => {
    if (isBenfordOrSampling && groupBy.length > 0) {
      onChangeGroupBy([]);
    }
  }, [isBenfordOrSampling]);

  /* ---------------------------------------------
   * Derived UI values
   * --------------------------------------------- */
  const groupByHintText = useMemo(() => {
    if (isBenfordOrSampling) {
      return "Not required for Analysis";
    }
    if (isRSF && !isArray) {
      return "Required for RSF Analysis (Select up to 3 columns for grouping)";
    }
    return `Select up to 3 columns for grouping (${groupBy.length}/3)`;
  }, [isBenfordOrSampling, isRSF, isArray, groupBy.length]);

  const groupByPlaceholder = isBenfordOrSampling
    ? "Disabled for analysis"
    : "Select columns to group by";

  const isGroupByDisabled = isBenfordOrSampling;

  const isRSFError =
    isRSF && !isArray && groupBy.length === 0;

  /* ---------------------------------------------
   * Render
   * --------------------------------------------- */
  return (
    <div className="bg-[#131314] rounded-lg border border-gray-700 p-6 h-full">
      <div className="flex items-center justify-center mb-6">
        <h2 className="text-2xl font-bold text-white flex items-center gap-2">
          <TrendingUp className="text-purple-500" size={28} />
          Analytics Configuration
        </h2>
      </div>

      {/* Model Selection */}
      <div className="mb-6">
        <div className="bg-gray-800 rounded-xl p-5 border-l-4 border-purple-500">
          <div className="flex justify-between mb-2">
            <label className="text-gray-300 font-semibold text-sm uppercase">
              Model Selection
            </label>
            <span className="text-xs text-purple-300">
              Select one model to analyze
            </span>
          </div>

          <Select
            showSearch
            value={modalSelection ?? MODEL_OPTIONS[0].value}
            onChange={onChangeModal}
            className="w-full"
            size="large"
            placeholder="Select a model"
            options={MODEL_OPTIONS}
            allowClear
          />
        </div>
      </div>

      <Divider />

      {/* Dependent Variable */}
      <div className="mb-6">
        <div className="bg-gray-800 rounded-xl p-5 border-l-4 border-purple-500">
          <div className="flex justify-between mb-2">
            <label className="text-gray-300 font-semibold text-sm uppercase">
              Dependent Variable
            </label>
            <span className="text-xs text-purple-300">
              Select one numeric column to analyze
            </span>
          </div>

          <Select
            showSearch
            value={dependentVariable}
            onChange={onChangeDependent}
            className="w-full"
            size="large"
            placeholder="Select a column"
            options={dependentOptions}
            allowClear
          />
        </div>
      </div>

      <Divider />

      {/* Group By Variables */}
      <div className="mb-6">
        <div className="bg-gray-800 rounded-xl p-5 border-l-4 border-purple-500">
          <div className="flex justify-between mb-2">
            <label className="text-gray-300 font-semibold text-sm uppercase">
              Group By Variables
            </label>
            <span className="text-xs text-purple-300">
              {groupByHintText}
            </span>
          </div>

          <Select
            showSearch
            mode="multiple"
            value={groupBy}
            onChange={onChangeGroupBy}
            maxTagCount={3}
            size="large"
            allowClear
            className="w-full"
            placeholder={groupByPlaceholder}
            options={groupByOptions}
            disabled={isGroupByDisabled}
            status={isRSFError ? "error" : ""}
          />
        </div>
      </div>

      {/* Actions */}
      <Space direction="vertical" className="w-full">
        <Button
          type="primary"
          size="large"
          className="w-full bg-[#9161ad] hover:!bg-[#BC9BCF]/90 text-white disabled:hover:!bg-[#BC9BCF]/90 disabled:text-white"
          onClick={() => {
            if (isRSFError) {
              message.error("Group By Variables are required for RSF analysis");
              return;
            }

            const models =
              modalSelection === "ensemble"
                ? ENSEMBLE_MODELS
                : [
                    String(modalSelection)
                      .toLowerCase()
                      .replace(/\s|-/g, ""),
                  ];

            onConfigure({
              models,
              dep_variable: dependentVariable,
              groupby_columns: groupBy,
            });
          }}
          loading={applying}
          disabled={isRSFError}
        >
          <Zap size={20} />
          Configure Analytics
        </Button>

        {applyResponse?.blob_download_link && (
          <Button
            size="large"
            className="w-full bg-green-600 hover:!bg-green-700 !text-white"
            disabled={applying}
            onClick={onBackToDashboard}
          >
            <FileOutput size={20} />
            View Analytics Results
          </Button>
        )}
      </Space>
    </div>
  );
};

export default AnalyticsConfigurationCard;